import { ReloadOutlined } from "@ant-design/icons";
import { Button, Checkbox, Collapse, Empty, Space, Spin, Tag, Typography, message } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { useApiContext } from "../../../utils";

import { createSmartAlertsDebugApi } from "./api";
import { AgentMessage, ToolCall } from "./types";

const { Text } = Typography;

const PanelWrapper = styled.div`
  background: #fafafa;
  border-radius: 6px;
  padding: 12px 16px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const PanelTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #444;
`;

const MessageCard = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;

  & + & {
    margin-top: 8px;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const MessageText = styled.div`
  color: #333;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CodeBlock = styled.pre`
  margin: 8px 0 0;
  padding: 8px 10px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  color: #333;
  font-size: 12px;
  max-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Screenshot = styled.img`
  display: block;
  max-width: 520px;
  max-height: 260px;
  margin-top: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
`;

const ROLE_COLOR: Record<string, string> = {
  user: "blue",
  agent: "purple",
  event: "gold",
};

const ROLE_LABEL: Record<string, string> = {
  user: "USER",
  agent: "AGENT",
  event: "EVENT",
};

interface ThreadPanelProps {
  alertId: string;
}

function formatDateTime(iso?: string): string {
  if (!iso) {
    return "-";
  }
  return new Date(iso).toLocaleString();
}

function formatToolArguments(toolCall: ToolCall): string {
  const args = toolCall.function?.arguments;
  if (!args) {
    return "";
  }
  try {
    return JSON.stringify(JSON.parse(args), null, 2);
  } catch {
    return args;
  }
}

function shortText(text: string, max = 220): string {
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max)}...`;
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export const ThreadPanel: React.FC<ThreadPanelProps> = ({ alertId }) => {
  const apiContext = useApiContext();
  const [threadId, setThreadId] = useState<string | undefined>();
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [hideEvents, setHideEvents] = useState(false);
  const [hideToolCalls, setHideToolCalls] = useState(false);
  const [userAndAssistantOnly, setUserAndAssistantOnly] = useState(false);

  const loadThread = useCallback(async () => {
    if (!apiContext?.DEPRECATED_api) {
      return;
    }
    const api = createSmartAlertsDebugApi(apiContext.DEPRECATED_api);
    setLoading(true);
    try {
      const response = await api.getThread(alertId);
      setThreadId(response.thread_id);
      setMessages(response.messages || []);
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to load agent thread");
    } finally {
      setLoading(false);
    }
  }, [alertId, apiContext]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  const visibleMessages = useMemo(() => {
    return messages.filter(msg => {
      if (userAndAssistantOnly && msg.role !== "user" && msg.role !== "agent") {
        return false;
      }
      if (hideEvents && msg.role === "event") {
        return false;
      }
      return true;
    });
  }, [messages, hideEvents, userAndAssistantOnly]);

  return (
    <PanelWrapper>
      <PanelHeader>
        <div>
          <PanelTitle>Agent Thread ({messages.length})</PanelTitle>
          {threadId && (
            <Text copyable={{ text: threadId }} style={{ fontSize: 11, color: "#999" }}>
              {threadId.slice(0, 8)}...
            </Text>
          )}
        </div>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={loadThread}
          loading={loading}
          type="text"
        >
          Refresh
        </Button>
      </PanelHeader>

      <Space size="middle" style={{ marginBottom: 10 }} wrap>
        <Checkbox checked={hideToolCalls} onChange={e => setHideToolCalls(e.target.checked)}>
          Hide tool calls
        </Checkbox>
        <Checkbox checked={hideEvents} onChange={e => setHideEvents(e.target.checked)}>
          Hide events
        </Checkbox>
        <Checkbox
          checked={userAndAssistantOnly}
          onChange={e => setUserAndAssistantOnly(e.target.checked)}
        >
          Only user + assistant
        </Checkbox>
      </Space>

      {loading ? (
        <Spin />
      ) : !threadId ? (
        <Empty
          description="No agent thread - this alert was configured directly or predates agent task linking."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : visibleMessages.length === 0 ? (
        <Empty
          description="No messages match the current filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        visibleMessages.map(msg => {
          const text = msg.content?.text || "";
          const reasoning = msg.content?.reasoning;
          const toolCalls = msg.content?.tool_calls || [];
          const role = msg.role || "agent";
          return (
            <MessageCard key={msg.id}>
              <MessageHeader>
                <Space>
                  <Tag color={ROLE_COLOR[role] || "default"}>{ROLE_LABEL[role] || role}</Tag>
                  {msg.content?.event_type && <Tag color="gold">{msg.content.event_type}</Tag>}
                  {toolCalls.length > 0 && !hideToolCalls && (
                    <Tag color="default">
                      {toolCalls.length} tool call{toolCalls.length === 1 ? "" : "s"}
                    </Tag>
                  )}
                </Space>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatDateTime(msg.created_dt)}
                </Text>
              </MessageHeader>

              {text && (
                <Collapse ghost defaultActiveKey={text.length > 500 ? [] : ["text"]}>
                  <Collapse.Panel header={shortText(text, 160)} key="text">
                    <MessageText>{text}</MessageText>
                  </Collapse.Panel>
                </Collapse>
              )}

              {reasoning && (
                <Collapse ghost defaultActiveKey={[]}>
                  <Collapse.Panel header="Reasoning" key="reasoning">
                    <MessageText>{reasoning}</MessageText>
                  </Collapse.Panel>
                </Collapse>
              )}

              {!hideToolCalls && toolCalls.length > 0 && (
                <Collapse ghost defaultActiveKey={[]}>
                  <Collapse.Panel header="Tool Calls" key="tools">
                    {toolCalls.map(toolCall => (
                      <div key={toolCall.id} style={{ marginBottom: 10 }}>
                        <Text strong style={{ fontSize: 12 }}>
                          {toolCall.function?.name || "tool"}
                        </Text>
                        <CodeBlock>{formatToolArguments(toolCall) || "(no arguments)"}</CodeBlock>
                      </div>
                    ))}
                  </Collapse.Panel>
                </Collapse>
              )}

              {msg.content?.web_dashboard_json && (
                <Collapse ghost defaultActiveKey={[]}>
                  <Collapse.Panel header="Dashboard JSON" key="dashboard-json">
                    <CodeBlock>{formatJson(msg.content.web_dashboard_json)}</CodeBlock>
                  </Collapse.Panel>
                </Collapse>
              )}

              {msg.content?.screenshot && (
                <Screenshot src={msg.content.screenshot} alt="Thread screenshot" />
              )}
            </MessageCard>
          );
        })
      )}
    </PanelWrapper>
  );
};
